import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  MessageCircle, 
  Send, 
  Plus, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Shield,
  X,
  Search,
  Filter,
  MoreVertical,
  Paperclip,
  Info,
  ChevronDown,
  Check
} from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../hooks/useAuth';

type ConversationStatus = 'open' | 'closed' | 'pending';
type ConversationPriority = 'low' | 'medium' | 'high' | 'urgent';
type ConversationStatusFilter = 'all' | ConversationStatus;

interface Conversation {
  id: string;
  user_id: string;
  subject: string;
  status: ConversationStatus;
  priority: ConversationPriority;
  assigned_admin_id: string | null;
  assigned_admin_first_name: string | null;
  assigned_admin_last_name: string | null;
  created_at: string;
  updated_at: string;
  unread_count?: number;
  last_message?: string;
  last_message_time?: string;
}

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  is_admin_message: boolean;
  created_at: string;
}

interface SupportChatProps {
  user: any;
}

interface DropdownOption {
  value: string;
  label: string;
  description?: string;
  accentClassName: string;
  count?: number;
}

interface SupportDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  menuLabel: string;
  leadingIcon?: React.ComponentType<{ size?: number; className?: string }>;
  className?: string;
}

const SupportDropdown: React.FC<SupportDropdownProps> = ({
  value,
  onChange,
  options,
  menuLabel,
  leadingIcon: LeadingIcon,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find(option => option.value === value) ?? options[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-purple-500/35 bg-[linear-gradient(135deg,rgba(12,17,38,0.995),rgba(26,33,70,0.992),rgba(41,25,74,0.99))] px-3 py-2.5 text-left shadow-lg shadow-slate-950/35 transition-all duration-200 hover:border-purple-400/50 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
      >
        <div className="flex min-w-0 items-center gap-3">
          {LeadingIcon ? (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-purple-500/25 bg-purple-950/85 text-purple-100 shadow-inner shadow-purple-500/10">
              <LeadingIcon size={16} className="text-purple-100" />
            </span>
          ) : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-purple-500/22 bg-slate-900/90">
              <span className={`h-2.5 w-2.5 rounded-full ${selectedOption.accentClassName}`} />
            </span>
          )}

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-white">{selectedOption.label}</span>
              {typeof selectedOption.count === 'number' && (
                <span className="rounded-full border border-purple-500/22 bg-purple-950/80 px-2 py-0.5 text-[11px] text-slate-300">
                  {selectedOption.count}
                </span>
              )}
            </div>
            {selectedOption.description && (
              <p className="truncate text-[11px] text-slate-400">{selectedOption.description}</p>
            )}
          </div>
        </div>

        <ChevronDown
          size={16}
          className={`shrink-0 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-purple-100' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-2 w-full min-w-[16rem] overflow-hidden rounded-2xl border border-purple-500/35 bg-[linear-gradient(180deg,rgba(17,26,64,0.998)_0%,rgba(20,31,74,0.996)_48%,rgba(27,18,60,0.995)_100%)] shadow-2xl shadow-slate-950/85">
          <div className="border-b border-purple-500/22 px-3 py-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
            {menuLabel}
          </div>
          <div className="p-2" role="listbox">
            {options.map((option) => {
              const isSelected = option.value === value;

              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all duration-200 ${
                    isSelected
                      ? 'bg-[linear-gradient(135deg,rgba(52,73,128,0.28),rgba(71,46,137,0.3))] shadow-inner shadow-purple-500/12'
                      : 'hover:bg-purple-500/10'
                  }`}
                >
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border ${
                    isSelected
                      ? 'border-purple-400/40 bg-purple-500/16'
                      : 'border-purple-500/20 bg-slate-900/78 group-hover:border-purple-400/30'
                  }`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${option.accentClassName}`} />
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`truncate text-sm font-medium ${isSelected ? 'text-white' : 'text-slate-200'}`}>
                        {option.label}
                      </span>
                      {typeof option.count === 'number' && (
                        <span className={`rounded-full px-2 py-0.5 text-[11px] ${
                          isSelected
                            ? 'bg-purple-400/15 text-purple-100'
                            : 'bg-slate-900/75 text-slate-400'
                        }`}>
                          {option.count}
                        </span>
                      )}
                    </div>
                    {option.description && (
                      <p className="truncate text-xs text-slate-400">{option.description}</p>
                    )}
                  </div>

                  {isSelected && <Check size={15} className="shrink-0 text-purple-100" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const SupportChat: React.FC<SupportChatProps> = ({ user: _user }) => {
  const { user: authUser } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessageContent, setNewMessageContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Agent info for the selected conversation
  const [assignedAgentInfo, setAssignedAgentInfo] = useState<{
    first_name: string | null;
    last_name: string | null;
  } | null>(null);
  
  // New conversation modal state
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [newConversationSubject, setNewConversationSubject] = useState('');
  const [newConversationPriority, setNewConversationPriority] = useState<ConversationPriority>('medium');
  const [creatingConversation, setCreatingConversation] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConversationStatusFilter>('all');
  
  // Refs for auto-scrolling
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!authUser) return;

    try {
      const { data, error } = await supabase
        .rpc('get_user_conversations', {
          p_user_id: authUser.id
        });

      if (error) throw error;

      setConversations(data || []);
    } catch (err: any) {
      console.error('Error fetching conversations:', err);
      setError('Failed to load conversations');
    }
  }, [authUser]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId: string) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setMessages(data || []);
      
      // Mark messages as read
      if (authUser) {
        await supabase.rpc('mark_chat_messages_read', {
          p_conversation_id: conversationId,
          p_user_id: authUser.id
        });
      }
    } catch (err: any) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages');
    }
  }, [authUser]);

  // Create new conversation
  const createConversation = async () => {
    if (!authUser || !newConversationSubject.trim()) return;

    setCreatingConversation(true);
    setError(null);

    try {
      const { data, error } = await supabase.rpc('create_chat_conversation', {
        p_user_id: authUser.id,
        p_subject: newConversationSubject.trim(),
        p_priority: newConversationPriority
      });

      if (error) throw error;

      if (data && data.length > 0) {
        const newConversation = data[0];
        setConversations(prev => [newConversation, ...prev]);
        setSelectedConversationId(newConversation.id);
        setShowNewConversationModal(false);
        setNewConversationSubject('');
        setNewConversationPriority('medium');
        setSuccess('New conversation created successfully');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err: any) {
      console.error('Error creating conversation:', err);
      setError('Failed to create conversation');
    } finally {
      setCreatingConversation(false);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!authUser || !selectedConversationId || !newMessageContent.trim()) return;

    setSendingMessage(true);
    setError(null);

    const messageContent = newMessageContent.trim();
    
    // Clear the input immediately for better UX
    setNewMessageContent('');
    
    try {
      const { data, error } = await supabase.rpc('send_chat_message', {
        p_conversation_id: selectedConversationId,
        p_sender_id: authUser.id,
        p_content: messageContent
      });

      if (error) throw error;

      // Create optimistic message object
      const optimisticMessage: Message = {
        id: data || `temp_${Date.now()}`,
        conversation_id: selectedConversationId,
        sender_id: authUser.id,
        content: messageContent,
        is_read: false,
        is_admin_message: false,
        created_at: new Date().toISOString()
      };

      // Optimistically add the message to the UI
      setMessages(prev => {
        return [...prev, optimisticMessage];
      });

      // Scroll to bottom immediately
      setTimeout(scrollToBottom, 100);
      
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
      
      // Restore the message content if there was an error
      setNewMessageContent(messageContent);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
      setImagePreviewUrl(URL.createObjectURL(file));
    }
  };

  const clearSelectedImage = () => {
    setSelectedImage(null);
    setImagePreviewUrl(null);
  };

  // Handle Enter key press in message input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Initial data fetch
  useEffect(() => {
    const initializeChat = async () => {
      setLoading(true);
      await fetchConversations();
      setLoading(false);
    };

    if (authUser) {
      initializeChat();
    }
  }, [authUser, fetchConversations]);

  // Fetch messages when conversation is selected
  useEffect(() => {
    if (selectedConversationId) {
      fetchMessages(selectedConversationId);
      
      // Update assigned agent info when conversation changes
      const selectedConv = conversations.find(conv => conv.id === selectedConversationId);
      if (selectedConv) {
        setAssignedAgentInfo({
          first_name: selectedConv.assigned_admin_first_name,
          last_name: selectedConv.assigned_admin_last_name
        });
      } else {
        setAssignedAgentInfo(null);
      }
    } else {
      setMessages([]);
      setAssignedAgentInfo(null);
    }
  }, [selectedConversationId, fetchMessages, conversations]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Set up realtime subscriptions
  useEffect(() => {
    if (!authUser) return;

    // Subscribe to new conversations
    const conversationsChannel = supabase
      .channel('user_conversations')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `user_id=eq.${authUser.id}`
        },
        (payload) => {
          console.log('Conversation change:', payload);
          fetchConversations();
        }
      )
      .subscribe();

    // Subscribe to messages in the selected conversation
    let messagesChannel: any = null;
    
    if (selectedConversationId) {
      messagesChannel = supabase
        .channel('conversation_messages')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${selectedConversationId}`
          },
          (payload) => {
            console.log('Message change:', payload);
            if (payload.eventType === 'INSERT') {
              setMessages(prev => {
                // Check if this message already exists (from optimistic update)
                const existingMessageIndex = prev.findIndex(msg => 
                  msg.id === payload.new.id ||
                  (msg.sender_id === payload.new.sender_id && 
                   msg.content === payload.new.content && 
                   Math.abs(new Date(msg.created_at).getTime() - new Date(payload.new.created_at).getTime()) < 5000)
                );
                
                if (existingMessageIndex >= 0) {
                  // Update the existing optimistic message with the confirmed data
                  const newMessages = [...prev];
                  newMessages[existingMessageIndex] = payload.new as Message;
                  return newMessages;
                } else {
                  // Add new message (likely from another user or admin)
                  return [...prev, payload.new as Message];
                }
              });
              
              // Scroll to bottom when new message arrives
              setTimeout(scrollToBottom, 100);
            } else if (payload.eventType === 'UPDATE') {
              setMessages(prev => prev.map(msg => 
                msg.id === payload.new.id ? payload.new as Message : msg
              ));
            }
          }
        )
        .subscribe();
    }

    return () => {
      conversationsChannel.unsubscribe();
      if (messagesChannel) {
        messagesChannel.unsubscribe();
      }
    };
  }, [authUser, selectedConversationId, fetchConversations]);

  // Filter conversations based on search and status
  const statusCounts: Record<ConversationStatusFilter, number> = {
    all: conversations.length,
    open: 0,
    pending: 0,
    closed: 0
  };

  conversations.forEach((conversation) => {
    statusCounts[conversation.status] += 1;
  });

  const statusFilterOptions: DropdownOption[] = [
    {
      value: 'all',
      label: 'All Conversations',
      description: 'Every thread in your inbox',
      accentClassName: 'bg-blue-400',
      count: statusCounts.all
    },
    {
      value: 'open',
      label: 'Open',
      description: 'Active conversations waiting on updates',
      accentClassName: 'bg-emerald-400',
      count: statusCounts.open
    },
    {
      value: 'pending',
      label: 'Pending',
      description: 'Conversations in progress with support',
      accentClassName: 'bg-amber-400',
      count: statusCounts.pending
    },
    {
      value: 'closed',
      label: 'Closed',
      description: 'Resolved conversations',
      accentClassName: 'bg-slate-400',
      count: statusCounts.closed
    }
  ];

  const priorityOptions: DropdownOption[] = [
    {
      value: 'low',
      label: 'Low Priority',
      description: 'General questions with no urgency',
      accentClassName: 'bg-slate-400'
    },
    {
      value: 'medium',
      label: 'Medium Priority',
      description: 'Standard support requests',
      accentClassName: 'bg-blue-400'
    },
    {
      value: 'high',
      label: 'High Priority',
      description: 'Important account or trading issues',
      accentClassName: 'bg-orange-400'
    },
    {
      value: 'urgent',
      label: 'Urgent',
      description: 'Time-sensitive problems that need fast help',
      accentClassName: 'bg-red-400'
    }
  ];

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || conv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Get selected conversation details
  const selectedConversation = conversations.find(conv => conv.id === selectedConversationId);

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-emerald-400 bg-emerald-400/10';
      case 'closed': return 'text-slate-400 bg-slate-400/10';
      case 'pending': return 'text-amber-400 bg-amber-400/10';
      default: return 'text-slate-400 bg-slate-400/10';
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-400 bg-red-400/10';
      case 'high': return 'text-orange-400 bg-orange-400/10';
      case 'medium': return 'text-blue-400 bg-blue-400/10';
      case 'low': return 'text-slate-400 bg-slate-400/10';
      default: return 'text-slate-400 bg-slate-400/10';
    }
  };

  // Format time
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const shellClass =
    'flex h-[600px] overflow-hidden rounded-2xl border border-purple-500/30 bg-gradient-to-br from-blue-500/20 via-indigo-500/16 to-purple-500/14 shadow-2xl shadow-purple-500/10 backdrop-blur-sm';
  const headerPanelClass =
    'border-b border-purple-500/20 bg-purple-950/18';
  const footerPanelClass =
    'border-t border-purple-500/20 bg-purple-950/18';
  const sidebarClass =
    'flex w-1/3 flex-col border-r border-purple-500/20 bg-slate-950/18';
  const inputClass =
    'purple-form-field w-full rounded-lg border border-purple-500/30 bg-gradient-to-r from-indigo-950/72 via-purple-950/72 to-fuchsia-950/70 text-white placeholder:text-slate-400 transition-all focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-400/55';
  const primaryButtonClass =
    'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-purple-500/25 transition-all duration-300 hover:from-purple-500 hover:to-fuchsia-500 disabled:from-slate-700 disabled:to-slate-800';
  const secondaryButtonClass =
    'border border-purple-500/25 bg-purple-950/40 text-slate-200 transition-all duration-300 hover:border-purple-400/40 hover:bg-purple-500/12 hover:text-white';
  const activeConversationClass =
    'border border-purple-400/35 bg-gradient-to-r from-indigo-500/18 via-purple-500/16 to-fuchsia-500/14 shadow-lg shadow-purple-500/10';
  const inactiveConversationClass =
    'border border-transparent bg-slate-950/30 hover:border-purple-500/20 hover:bg-purple-950/24';
  const modalClass =
    'w-full max-w-md rounded-2xl border border-purple-500/35 bg-[linear-gradient(180deg,rgba(6,10,24,0.995)_0%,rgba(13,18,40,0.992)_46%,rgba(28,14,54,0.99)_100%)] p-6 shadow-2xl shadow-black/75 ring-1 ring-white/5';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-purple-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      {/* Conversations Sidebar */}
      <div className={sidebarClass}>
        {/* Sidebar Header */}
        <div className={`p-4 ${headerPanelClass}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <MessageCircle size={20} className="text-purple-200" />
              Support Chat
            </h3>
            <button
              onClick={() => setShowNewConversationModal(true)}
              className={`rounded-lg p-2 ${primaryButtonClass}`}
            >
              <Plus size={16} />
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${inputClass} py-2 pl-10 pr-4 text-sm`}
            />
          </div>

          {/* Status Filter */}
          <SupportDropdown
            value={statusFilter}
            onChange={(value) => setStatusFilter(value as ConversationStatusFilter)}
            options={statusFilterOptions}
            menuLabel="Conversation status"
            leadingIcon={Filter}
          />
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length > 0 ? (
            <div className="space-y-1 p-2">
              {filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => setSelectedConversationId(conversation.id)}
                  className={`cursor-pointer rounded-lg p-3 transition-all duration-300 ${
                    selectedConversationId === conversation.id
                      ? activeConversationClass
                      : inactiveConversationClass
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-white text-sm line-clamp-1">
                      {conversation.subject}
                    </h4>
                    <div className="flex items-center gap-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(conversation.status)}`}>
                        {conversation.status}
                      </span>
                      {conversation.unread_count && conversation.unread_count > 0 && (
                        <span className="min-w-[18px] rounded-full bg-red-500 px-1.5 py-0.5 text-center text-xs text-white">
                          {conversation.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(conversation.priority)}`}>
                      {conversation.priority}
                    </span>
                    <span className="text-xs text-slate-400">
                      {formatTime(conversation.updated_at)}
                    </span>
                  </div>
                  
                  {conversation.last_message && (
                    <p className="text-xs text-slate-400 mt-2 line-clamp-2">
                      {conversation.last_message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-500 p-4">
              <MessageCircle size={48} className="mb-4 text-purple-200/50" />
              <p className="text-center mb-2">No conversations found</p>
              <p className="text-sm text-slate-600 text-center mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter' 
                  : 'Start a new conversation to get help'}
              </p>
              <button
                onClick={() => setShowNewConversationModal(true)}
                className={`rounded-lg px-4 py-2 text-sm font-medium ${primaryButtonClass}`}
              >
                Start New Chat
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className={`p-4 ${headerPanelClass}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-white">{selectedConversation.subject}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(selectedConversation.status)}`}>
                      {selectedConversation.status}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(selectedConversation.priority)}`}>
                      {selectedConversation.priority} priority
                    </span>
                    {assignedAgentInfo && (assignedAgentInfo.first_name || assignedAgentInfo.last_name) && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">
                        Dedicated Manager
                      </span>
                    )}
                    <span className="text-xs text-slate-400">
                      Created {formatTime(selectedConversation.created_at)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button className="rounded-lg border border-purple-500/15 bg-purple-950/28 p-2 text-slate-400 transition-colors hover:text-white">
                    <Info size={16} />
                  </button>
                  <button className="rounded-lg border border-purple-500/15 bg-purple-950/28 p-2 text-slate-400 transition-colors hover:text-white">
                    <MoreVertical size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length > 0 ? (
                <>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.sender_id === authUser?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${
                        message.sender_id === authUser?.id
                          ? 'bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 text-white'
                          : 'border border-purple-500/20 bg-gradient-to-br from-indigo-500/12 via-purple-500/12 to-fuchsia-500/12 text-white'
                      } rounded-2xl px-4 py-3 shadow-lg`}>
                        {message.sender_id !== authUser?.id && (
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                              <Shield size={12} className="text-white" />
                            </div>
                            <span className="text-xs font-medium text-slate-300">
                              Fidelity Investment Support
                            </span>
                          </div>
                        )}
                        
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                          {message.content}
                        </p>
                        
                        <div className={`flex items-center justify-between mt-2 text-xs ${
                          message.sender_id === authUser?.id ? 'text-blue-100' : 'text-slate-400'
                        }`}>
                          <span>{formatTime(message.created_at)}</span>
                          {message.sender_id === authUser?.id && (
                            <div className="flex items-center gap-1">
                              {message.is_read ? (
                                <CheckCircle size={12} className="text-blue-200" />
                              ) : (
                                <Clock size={12} className="text-blue-200" />
                              )}
                              <span>{message.is_read ? 'Read' : 'Sent'}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <MessageCircle size={48} className="mb-4 text-purple-200/50" />
                  <p className="text-center mb-2">No messages yet</p>
                  <p className="text-sm text-slate-600 text-center">
                    Start the conversation by sending a message below
                  </p>
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className={`p-4 ${footerPanelClass}`}>
              {selectedConversation.status === 'closed' ? (
                <div className="rounded-xl border border-purple-500/20 bg-purple-950/30 p-4 text-center">
                  <p className="text-slate-400 text-sm">This conversation has been closed</p>
                  <p className="text-slate-500 text-xs mt-1">Start a new conversation to continue getting support</p>
                </div>
              ) : (
                <div className="flex items-end gap-3">
                  <input
                    type="file"
                    id="image-upload"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <label
                    htmlFor="image-upload"
                    className={`flex cursor-pointer items-center justify-center rounded-xl p-3 text-white ${secondaryButtonClass}`}
                  >
                    <Paperclip size={16} />
                  </label>

                  <div className="flex-1">
                    <textarea
                      ref={messageInputRef}
                      value={newMessageContent}
                      onChange={(e) => setNewMessageContent(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      className={`${inputClass} resize-none px-4 py-3`}
                      rows={1}
                      style={{ minHeight: '44px', maxHeight: '120px' }}
                      onInput={(e) => {
                        const target = e.target as HTMLTextAreaElement;
                        target.style.height = 'auto';
                        target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                      }}
                    />
                    {imagePreviewUrl && (
                      <div className="relative mt-2 flex items-center justify-between rounded-lg border border-purple-500/20 bg-purple-950/35 p-2">
                        <img src={imagePreviewUrl} alt="Image Preview" className="max-h-24 rounded-md" />
                        <button
                          onClick={clearSelectedImage}
                          className="absolute top-1 right-1 bg-black/50 rounded-full p-1 text-white hover:bg-black/70"
                        >
                          <X size={14} />
                        </button>
                        <span className="ml-2 text-sm text-slate-300">
                          {selectedImage ? selectedImage.name : ''}
                        </span>
                      </div>
                    )}


                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={sendMessage}
                      disabled={sendingMessage || !newMessageContent.trim()}
                      className={`flex items-center justify-center rounded-xl p-3 ${primaryButtonClass}`}
                    >
                      {sendingMessage ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Send size={16} />
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          /* No Conversation Selected */
          <div className="flex flex-col items-center justify-center h-full text-slate-500">
            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border border-purple-500/20 bg-purple-950/30">
              <MessageCircle size={32} className="text-purple-200/70" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Welcome to Support Chat</h3>
            <p className="text-slate-400 text-center mb-6 max-w-md">
              Get help from our support team. Select an existing conversation or start a new one to begin.
            </p>
            <button
              onClick={() => setShowNewConversationModal(true)}
              className={`flex items-center gap-2 rounded-xl px-6 py-3 font-semibold ${primaryButtonClass}`}
            >
              <Plus size={18} />
              Start New Conversation
            </button>
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewConversationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/76 p-4 backdrop-blur-md">
          <div className={modalClass}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">New Support Conversation</h3>
              <button
                onClick={() => setShowNewConversationModal(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Subject</label>
                <input
                  type="text"
                  value={newConversationSubject}
                  onChange={(e) => setNewConversationSubject(e.target.value)}
                  placeholder="What do you need help with?"
                  className={`${inputClass} px-4 py-3`}
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Priority</label>
                <SupportDropdown
                  value={newConversationPriority}
                  onChange={(value) => setNewConversationPriority(value as ConversationPriority)}
                  options={priorityOptions}
                  menuLabel="Set priority"
                />
              </div>

              <div className="rounded-xl border border-purple-500/25 bg-[linear-gradient(135deg,rgba(25,16,51,0.92),rgba(44,18,86,0.86))] p-3 text-sm text-purple-200 shadow-inner shadow-purple-500/10">
                <p className="font-medium mb-1">Support Guidelines</p>
                <ul className="text-xs space-y-1">
                  <li>• Be specific about your issue</li>
                  <li>• Include relevant details (transaction IDs, error messages)</li>
                  <li>• Our team typically responds within 2-4 hours</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowNewConversationModal(false)}
                  disabled={creatingConversation}
                  className={`flex-1 rounded-xl py-3 font-medium ${secondaryButtonClass}`}
                >
                  Cancel
                </button>
                <button
                  onClick={createConversation}
                  disabled={creatingConversation || !newConversationSubject.trim()}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-3 font-semibold ${primaryButtonClass}`}
                >
                  {creatingConversation ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    'Start Conversation'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Messages */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3 z-50">
          <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
          <span className="text-red-400">{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-300 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed bottom-4 right-4 bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3 z-50">
          <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
          <span className="text-green-400">{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="text-green-400 hover:text-green-300 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SupportChat;
